/**
 * setMatch — the pure, STRICT set-labeling matcher.
 *
 * matchSet(laps) walks SET_LIBRARY most-specific → general and returns the FIRST
 * pattern's label, or null when NOTHING matches confidently. Null means "leave the
 * existing title untouched" — the matcher NEVER guesses (owner decision: a false
 * positive is worse than no label; proven on the June-8 free-framed palindrome).
 *
 * Every Butterfly slot routes through `slotMatches`, which bakes in the one
 * wildcard the watch forces on us: an "Unknown" length satisfies a slot that
 * EXPECTS Butterfly (the watch's habitual fly-mislabel). "Unknown" wildcards
 * nothing else.
 *
 * Pure module (input Lap[], output {id,label}|null; no side effects, no Svelte
 * runtime), per lib convention.
 */
import {
  SET_LIBRARY,
  slotMatches,
  isBStroke,
  canonicalBStroke,
  IM_ORDER,
  FREE,
  type Lap,
  type SetDefinition,
} from "./setLibrary";

/**
 * A length is "no-arms" (kick/drill) when its MEASURED SWOLF implies almost no
 * stroke work AND it is long enough to be a real length (not a phantom split).
 *
 * ABSENT SWOLF IS UNKNOWN, NEVER "NO ARMS" (roadmap 34, 2026-09-09). This used to
 * return true for `swolf === null`, so every 2+ length set the watch gave no SWOLF
 * for read as "Kick / drill" — the whole open-water fixture, and any clubmate on a
 * watch that omits the field. Measured on the 113-export corpus: 202 of 7,556 real
 * lengths carry no SWOLF, and 117 of those carry a measured stroke count above 3,
 * i.e. provably arms swimming. A missing field is missing data.
 *
 * So an unmeasured length is neither evidence for nor against, and since
 * matchKickDrill needs a strict MAJORITY of all the set's lengths, it can only
 * prevent a match, never create one.
 */
function isNoArmsLength(lap: Lap): boolean {
  if (lap.durationSeconds < 15) return false; // glitch guard: phantom short laps
  if (lap.swolf === null) return false; // unknown, not zero
  return lap.swolf - lap.durationSeconds <= 3; // implied strokes <= 3
}

// ── Per-pattern predicates. Each returns a label string on a confident match,
//    else null. Small and readable; three similar comparisons beat an abstraction.

/** 1. Rockalita: exactly 8 lengths, [Fl,Bk,Br,*,*,Br,Bk,Fl], both middles same. */
function matchRockalita(laps: Lap[]): string | null {
  if (laps.length !== 8) return null;
  const s = laps.map((l) => l.strokeStyle);
  const okBookends =
    slotMatches("Butterfly", s[0]) &&
    s[1] === "Backstroke" &&
    s[2] === "Breaststroke" &&
    s[5] === "Breaststroke" &&
    s[6] === "Backstroke" &&
    slotMatches("Butterfly", s[7]);
  if (!okBookends) return null;
  // The two middle lengths must be the SAME single stroke (palindrome spine).
  if (s[3] !== s[4]) return null;
  return "The Rockalita";
}

/** 2/3. IM groups: length % 4 === 0 (>=4); every group of 4 matches `order`. */
function matchImGroups(laps: Lap[], order: readonly string[], label: string): string | null {
  if (laps.length < 4 || laps.length % 4 !== 0) return null;
  for (let i = 0; i < laps.length; i++) {
    const expected = order[i % 4];
    if (!slotMatches(expected, laps[i].strokeStyle)) return null;
  }
  return label;
}

/** 3a/3b. Template groups: length % template.length === 0 (>=1 group); every group
 *  matches `template` slot-for-slot (Butterfly slots accept the Unknown wildcard).
 *  Generic version of matchImGroups for arbitrary-length repeating sequences. */
function matchTemplateGroups(laps: Lap[], template: readonly string[], label: string): string | null {
  const n = template.length;
  if (n === 0 || laps.length < n || laps.length % n !== 0) return null;
  for (let i = 0; i < laps.length; i++) {
    if (!slotMatches(template[i % n], laps[i].strokeStyle)) return null;
  }
  return label;
}

/** 1b. Butterfly Sandwich: exactly 8 lengths, [Fl, Br, A, B, B, A, Br, Fl] — Fly
 *  bread + Breast spread bookends around a 4-length palindrome filling (pos2==pos5,
 *  pos3==pos4, any strokes). Fly bookends accept the Unknown wildcard. */
function matchButterflySandwich(laps: Lap[]): string | null {
  if (laps.length !== 8) return null;
  const s = laps.map((l) => l.strokeStyle);
  const bread =
    slotMatches("Butterfly", s[0]) &&
    slotMatches("Butterfly", s[7]) &&
    s[1] === "Breaststroke" &&
    s[6] === "Breaststroke";
  if (!bread) return null;
  if (s[2] !== s[5] || s[3] !== s[4]) return null; // filling must be a palindrome
  return "Butterfly Sandwich";
}

/** 1c. Exact sequence: laps.length === template.length and every slot passes
 *  slotMatches (Fly slots accept the Unknown wildcard). ONE repetition only —
 *  deliberately NOT templateGroups, which matches integer multiples (a double
 *  King or double Mosesso must not read as the single set). */
function matchExactSequence(laps: Lap[], template: readonly string[], label: string): string | null {
  if (template.length === 0 || laps.length !== template.length) return null;
  for (let i = 0; i < laps.length; i++) {
    if (!slotMatches(template[i], laps[i].strokeStyle)) return null;
  }
  return label;
}

/** 2. 4×125: groups of 5 — four Freestyle + a 5th "unique 25" cycling IM order
 *  (Fly, Back, Breast, Free) across reps. >=2 reps. Fly slot accepts Unknown.
 *  Labeled "{reps}×125" (assumes 25m pool). */
function matchReps125(laps: Lap[]): string | null {
  if (laps.length < 10 || laps.length % 5 !== 0) return null;
  const reps = laps.length / 5;
  for (let g = 0; g < reps; g++) {
    for (let k = 0; k < 4; k++) {
      if (laps[g * 5 + k].strokeStyle !== FREE) return null;
    }
    if (!slotMatches(IM_ORDER[g % 4], laps[g * 5 + 4].strokeStyle)) return null;
  }
  return `${reps}×125`;
}

/** 3. Book-ended {set}: exactly 2 lengths of one stroke X on EACH end (same X both
 *  ends), wrapping a core set that is matched RECURSIVELY. Label "Book-ended {core}".
 *  Guard: the middle must match a named pattern AND must not be merely a single-stroke
 *  block of the SAME stroke X (so an all-free set is NOT "Book-ended Straight
 *  Freestyle"). Recursion is broken by matchSetCore(middle, allowBookEnd=false). */
function matchBookEnded(laps: Lap[]): string | null {
  if (laps.length < 6) return null;
  const s = laps.map((l) => l.strokeStyle);
  const x = s[0];
  const n = laps.length;
  // 2-length book-ends, same single stroke X on both ends.
  if (s[1] !== x || s[n - 2] !== x || s[n - 1] !== x) return null;
  const middle = laps.slice(2, n - 2);
  if (middle.length === 0) return null;
  const middleResult = matchSetCore(middle, false);
  if (middleResult === null) return null;
  const middleLabel = middleResult.label;
  // Don't book-end a block that's just more of the SAME stroke (e.g. all free).
  const xSingleLabel = x === FREE ? "Straight Freestyle" : x;
  if (middleLabel === xSingleLabel) return null;
  const core = middleLabel.startsWith("The ") ? middleLabel.slice(4) : middleLabel;
  return `Book-ended ${core}`;
}

/** 4. Killer BS: all B-strokes, NO Free; >=2 distinct B-strokes each in >=2 lengths;
 *     no single stroke >75% of the set. */
function matchKillerBs(laps: Lap[]): string | null {
  if (laps.length < 2) return null;
  // Every length must be a B-stroke (no Freestyle anywhere).
  if (!laps.every((l) => isBStroke(l.strokeStyle))) return null;
  // Count occurrences per canonical B-stroke (Unknown counts as Fly).
  const counts = new Map<string, number>();
  for (const l of laps) {
    const c = canonicalBStroke(l.strokeStyle);
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  // >=2 DISTINCT strokes each appearing in >=2 lengths.
  const recurring = [...counts.values()].filter((n) => n >= 2).length;
  if (recurring < 2) return null;
  // No single stroke may exceed 75% of the set (strictly under).
  const maxShare = Math.max(...counts.values()) / laps.length;
  if (maxShare >= 0.75) return null;
  return "Killer BS";
}

/** 5. Kick / drill: >=2 MEASURED no-arms lengths forming the MAJORITY (> half) of
 *  the set. Lengths with absent SWOLF stay in the denominator (see isNoArmsLength),
 *  so partial coverage can still match — the floor is "more than half the set is
 *  measured AND no-arms", not a separate coverage constant. */
function matchKickDrill(laps: Lap[]): string | null {
  if (laps.length < 2) return null;
  const noArms = laps.filter(isNoArmsLength).length;
  if (noArms >= 2 && noArms > laps.length / 2) return "Kick / drill";
  return null;
}

/** 6. Stroke/Free alternating: >=4 lengths, strict Fr/X alternation, X is the one
 *     fixed non-free stroke. Label "{X} / Free". */
function matchStrokeFreeAlternating(laps: Lap[]): string | null {
  if (laps.length < 4) return null;
  const s = laps.map((l) => l.strokeStyle);
  const nonFree = s.filter((x) => x !== FREE);
  if (nonFree.length === 0) return null; // all Free → not "stroke/free"
  const x = nonFree[0];
  if (x === FREE) return null;
  if (!nonFree.every((v) => v === x)) return null; // must be ONE fixed non-free stroke
  // Strict alternation: no two adjacent the same; only Fr and X present.
  for (let i = 0; i < s.length; i++) {
    if (s[i] !== FREE && s[i] !== x) return null; // no third stroke
    if (i > 0 && s[i] === s[i - 1]) return null; // no two adjacent same
  }
  return `${x} / Free`;
}

/** 7. Single-stroke block: >=2 lengths, all the same stroke. */
function matchSingleStrokeBlock(laps: Lap[]): string | null {
  if (laps.length < 2) return null;
  const first = laps[0].strokeStyle;
  if (!laps.every((l) => l.strokeStyle === first)) return null;
  return first === FREE ? "Straight Freestyle" : first;
}

/** Dispatch one SET_LIBRARY entry to its predicate. Returns {id, label} on match. */
function runDefinition(def: SetDefinition, laps: Lap[]): { id: string; label: string } | null {
  let label: string | null;
  switch (def.kind) {
    case "rockalita":
      label = matchRockalita(laps);
      break;
    case "butterflySandwich":
      label = matchButterflySandwich(laps);
      break;
    case "reps125":
      label = matchReps125(laps);
      break;
    case "bookEnded":
      label = matchBookEnded(laps);
      break;
    case "imGroups": {
      const order = (def.params?.order as readonly string[]) ?? IM_ORDER;
      const imLabel = (def.params?.label as string) ?? "IM";
      label = matchImGroups(laps, order, imLabel);
      break;
    }
    case "exactSequence": {
      const template = (def.params?.template as readonly string[]) ?? [];
      const seqLabel = (def.params?.label as string) ?? "";
      label = matchExactSequence(laps, template, seqLabel);
      break;
    }
    case "templateGroups": {
      const template = (def.params?.template as readonly string[]) ?? [];
      const tmplLabel = (def.params?.label as string) ?? "";
      label = matchTemplateGroups(laps, template, tmplLabel);
      break;
    }
    case "killerBs":
      label = matchKillerBs(laps);
      break;
    case "kickDrill":
      label = matchKickDrill(laps);
      break;
    case "strokeFreeAlternating":
      label = matchStrokeFreeAlternating(laps);
      break;
    case "singleStrokeBlock":
      label = matchSingleStrokeBlock(laps);
      break;
    default:
      label = null;
  }
  return label !== null ? { id: def.id, label } : null;
}

/**
 * matchSet — classify a set's ordered laps into a named pattern, or null.
 *
 * Walks SET_LIBRARY most-specific → general; returns {id, label} for the first
 * confident match. Returns null when no pattern matches (STRICT: never guess).
 */
export function matchSet(laps: Lap[]): { id: string; label: string } | null {
  return matchSetCore(laps, true);
}

/** Core walk. `allowBookEnd` is false on the recursive call from matchBookEnded so
 *  the book-ended kind never re-enters itself (one level of nesting only). */
function matchSetCore(laps: Lap[], allowBookEnd: boolean): { id: string; label: string } | null {
  if (laps.length === 0) return null;
  for (const def of SET_LIBRARY) {
    if (def.kind === "bookEnded" && !allowBookEnd) continue;
    const result = runDefinition(def, laps);
    if (result !== null) return result;
  }
  return null;
}
